<template>
  <modal
    :show.sync="showModal"
    :disable-background-close="true"
    @close="handleClose"
  >
    <div v-if="person">
      <div class="modal-header">
        <h5 class="header-name" @click="goToPerson(person.id)">
          {{ person.name }}
        </h5>
        <div class="header-details">
          <badge :class="getBadgeClass(person.involvement)">
            {{ getAbbreviatedInvolvement(person.involvement) }}
          </badge>
          <span><strong>{{ person.gender }}</strong></span>
          <span><strong>{{ person.school_year }}</strong></span>
          <span><strong>{{ getColumnNameFromId(person.recruitment_depth_chart_column_id) }}</strong></span>
          <span v-if="person.is_greek"><strong>Greek</strong></span>
          <span v-if="person.is_varsity"><strong>Varsity</strong></span>
          <span>Last updated: {{ formattedLastUpdatedAt }}</span>
        </div>
      </div>
      <div
        class="modal-body"
        v-if="loading"
        v-loading="loading"
      >
      </div>
      <div v-else class="modal-body">
        <el-form>
          <el-form-item>
            Notes
            <el-input
              type="textarea"
              v-model="notes"
              :autosize="{ minRows: 3, maxRows: 5 }"
              placeholder="Add notes here..."
            ></el-input>
          </el-form-item>
          <el-form-item>
            <div class="open-to-call-row">
              <span class="open-to-call-label">Open to a call with a recruitment manager or regional director?</span>
              <div class="open-to-call-controls">
                <el-radio-group v-model="openToRecruitmentManagerCall">
                  <el-radio :label="true">
                    Yes
                  </el-radio>
                  <el-radio :label="false">
                    No
                  </el-radio>
                </el-radio-group>
                <el-button
                  v-if="openToRecruitmentManagerCall !== null"
                  type="text"
                  icon="el-icon-close"
                  class="clear-open-to-call"
                  @click="openToRecruitmentManagerCall = null"
                >Clear</el-button>
              </div>
            </div>
          </el-form-item>
          <el-form-item>
            If declined invitation to apply for FOCUS staff, please select the reason(s) below:
            <el-select v-model="selectedReasons" multiple placeholder="Select reason(s)">
              <el-option
                v-for="reason in reasons"
                :key="reason.value"
                :label="reason.label"
                :value="reason.value"
              ></el-option>
            </el-select>
          </el-form-item>
          <el-form-item
            v-if="selectedReasons && selectedReasons.includes(reasons.find(reason => reason.label === 'Other').value)"
          >
            If other, please provide more information:
            <el-input
              type="text"
              v-model="otherReasons"
              placeholder="Specify other reasons..."
            ></el-input>
          </el-form-item>
        </el-form>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button
          type="submit"
          class="btn btn-info"
          @click="saveNotes"
          v-loading="saving"
        >Save</el-button>
        <el-button class="btn" @click.native="handleClose">Close</el-button>
      </span>
    </div>
  </modal>
</template>

<script>
import { Badge } from 'src/components/UIComponents';
import { mapState, mapGetters } from 'vuex';
import Modal from '../UIComponents/Modal';
import { RepositoryFactory } from '../../repositories/repositoryFactory';

const RecruitmentDepthChartRepository = RepositoryFactory.get('recruitmentDepthCharts');

export default {
    name: 'RecruitmentNotesModal',
    components: {
        Badge,
        Modal
    },
    data() {
        return {
            showModal: false,
            notes: '', // Initial notes are empty
            selectedReasons: [], // An array to hold multiple selections
            otherReasons: '', // Holds the custom reason if "other" is selected
            openToRecruitmentManagerCall: null, // null = no action taken, true/false = explicit answer
            reasons: [ // Possible reasons for declined invitation
                { label: 'MPD', value: 78 },
                { label: 'Dating Fast', value: 79 },
                { label: 'Relocation', value: 80 },
                { label: 'Other Job Opportunity', value: 81 },
                { label: 'Fear of Career Impact', value: 82 },
                { label: 'Continuing Education', value: 83 },
                { label: 'Does Not Feel Called', value: 84 },
                { label: 'Pursuing Religious Vocation', value: 85 },
                { label: 'Other', value: 86 }
            ],
            lastUpdatedAt: '',
            saving: false,
            loading: false,
        };
    },
    computed: {
        ...mapState({
            locations: (state) => state.userLocations,
        }),
        ...mapGetters({
            locationFromStore: 'lastLocation'
        }),
        locationId: {
            get() {
                return this.locationFromStore;
            },
            set(newLocation) {
                this.$store.commit('setLastLocation', newLocation);
            }
        },
        formattedLastUpdatedAt() {
            if (!this.lastUpdatedAt) return '';
            let date;
            if (this.lastUpdatedAt.includes('T')) {
                // ISO format
                date = new Date(this.lastUpdatedAt);
            } else {
                // Legacy format: 'YYYY-MM-DD HH:mm:ss'
                const isoUtc = `${this.lastUpdatedAt.replace(' ', 'T')}Z`;
                date = new Date(isoUtc);
            }
            // Format without seconds
            const options = {
                year: 'numeric', month: 'short', day: 'numeric',
            };
            return date.toLocaleString(undefined, options);
        },
    },
    watch: {
        // React to changes in the prop from the parent
        show(val) {
            this.showModal = val;
            if (this.showModal) {
                this.getRecruitmentNotes();
                this.getLastUpdatedAt();
            }
        }
    },
    props: {
        show: Boolean,
        person: {
            type: Object,
            // eslint-disable-next-line
            default: {}
        },
        getBadgeClass: {
            type: Function,
            required: true
        },
        getAbbreviatedInvolvement: {
            type: Function,
            required: true
        },
    },
    methods: {
        handleClose() {
            this.$emit('close');
            this.notes = '';
            this.selectedReasons = [];
            this.otherReasons = '';
            this.openToRecruitmentManagerCall = null;
        },
        saveNotes() {
            // Check if the column_id is 'declined-invitation' and selectedReasons is empty
            const needToSpecifyDeclinedInvitationReason = this.doTheyNeedToSpecifyAReasonForDeclinedInvitation();
            if (needToSpecifyDeclinedInvitationReason) {
                // eslint-disable-next-line
                this.$message.error('Please select at least one reason they may have indicated for declining an invitation to apply for FOCUS staff.');
                return; // Prevent the method from proceeding
            }
            this.saving = true;
            RecruitmentDepthChartRepository.storeRecruitmentNotes(
                this.person.id,
                this.notes,
                this.selectedReasons,
                this.otherReasons,
                this.openToRecruitmentManagerCall,
                this.locationId
            ).then((response) => {
                this.saving = false;
                this.handleClose();
                this.$message(`Successfully saved notes for ${this.person.name}`);
                // Emit event to parent to update last updated time
                this.$emit('note-saved');
            }).catch((error) => {
                this.saving = false;
                this.handleClose();
                this.$message.error(error.response.data.message);
            });
        },
        getColumnNameFromId(id) {
            // Map the recruitment depth chart column id to the column name
            // can be either id string or number string based on user interaction
            switch (id) {
            case 72:
                return 'Potential Candidate';
            case 73:
                return 'Top Candidate';
            case 74:
                return 'Invited to Apply';
            case 75:
                return 'Applied';
            case 76:
                return 'Declined Invitation';
            case 77:
                return 'Not Invited to Apply';
            case 95:
                return 'Hired';
            case 96:
                return 'Not Hired';
            case 'potential-candidates':
                return 'Potential Candidate';
            case 'top-candidates':
                return 'Top Candidate';
            case 'invited-to-apply':
                return 'Invited to Apply';
            case 'applied':
                return 'Applied';
            case 'declined-invitation':
                return 'Declined Invitation';
            case 'not-invited-to-apply':
                return 'Not Invited to Apply';
            case 'hired':
                return 'Hired';
            case 'not-hired':
                return 'Not Hired';
            default:
                return id;
            }
        },
        getRecruitmentNotes() {
            this.loading = true;
            RecruitmentDepthChartRepository.getPersonsRecruitmentNotes(this.person.id)
                .then((response) => {
                    this.notes = response.data.notes;
                    this.selectedReasons = response.data.declined_invitation_reasons_ids;
                    this.otherReasons = response.data.other_reasons;
                    const openToCall = response.data.open_to_recruitment_manager_call;
                    this.openToRecruitmentManagerCall = typeof openToCall === 'boolean' ? openToCall : null;
                    this.loading = false;
                })
                .catch((error) => {
                    this.loading = false;
                    this.handleClose();
                    this.$message.error(`There was an error fetching notes for ${this.person.name}`);
                });
        },
        getLastUpdatedAt() {
            if (this.person) {
                RecruitmentDepthChartRepository.getMemberLastUpdatedAt(this.person.id)
                    .then((response) => {
                        this.lastUpdatedAt = response.data.last_updated_at;
                    })
                    .catch((error) => {
                        console.error(`Error fetching last updated at for ${this.person.name}:`, error);
                    });
            }
        },
        goToPerson(id) {
            this.$router.push(`/people/${id}`);
        },
        doTheyNeedToSpecifyAReasonForDeclinedInvitation() {
            // Check if the column_id is 'declined-invitation' and selectedReasons is empty
            // eslint-disable-next-line
            if (this.getColumnNameFromId(this.person.recruitment_depth_chart_column_id) === 'Declined Invitation' && this.selectedReasons.length === 0) {
                return true;
            }
            return false;
        }
    },
};
</script>

  <style scoped>
  .badge-na {
    background-color: #4d4f54; /* Starting color */
    color: white;
}

.badge-bspx1 {
  background-color: #6bb0e1; /* BSPx1 - Light blue */
  color: white;
}

.badge-bspx4 {
  background-color: #5aa4d9; /* Intermediate color */
  color: white;
}

.badge-growing-disciple {
  background-color: #4b97d1; /* Intermediate color */
  color: white;
}

.badge-commissioned-disciple {
  background-color: #3d89c9; /* Intermediate color */
  color: white;
}

.badge-slbs {
  background-color: #307bc1; /* Intermediate color */
  color: white;
}

.badge-growing-disciple-maker {
  background-color: #236eb9; /* Intermediate color */
  color: white;
}

.badge-disciple-maker {
  background-color: #0c3c61; /* Dark blue */
  color: white;
}
.modal-header {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 10px;
}

.header-details {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
  flex-wrap: wrap; /* Allow wrapping as needed */
}
.header-details > span {
  white-space: nowrap; /* Prevents the gender from wrapping */
}
.modal-body {
  display: flex;
}
.dialog-footer {
  text-align: right;
  display: flex;
  justify-content: flex-end; /* Aligns the content to the end of the flex container */
}

.header-name:hover {
  cursor: pointer;
  color: #0c3c61;
}

.open-to-call-row {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.open-to-call-label {
  line-height: 1.4;
}

.open-to-call-controls {
  display: flex;
  align-items: baseline;
  gap: 15px;
  line-height: 1.4;
}

.clear-open-to-call {
  padding: 0;
  font-size: 12px;
  color: #909399;
}

.clear-open-to-call:hover,
.clear-open-to-call:focus {
  color: #0c3c61;
}

</style>
